import { memo } from 'react';

import { useTranslation } from 'react-i18next';

import { useDeskId } from '@/hooks/useDeskId';
import { useDepartments } from '@/modules/departments/hooks/useDepartments';
import {
  ConversationEvent,
  ConversationEventChannelSwitch,
  ConversationEventTag,
} from '@/shared/types/chat';
import { IntegrationType } from '@/shared/types/integration';
import { getChannelKey } from '@/util/channelName';
import { formatTimeOnly } from '@/util/dateUtils';

import {
  isConversationEventAgent,
  isConversationEventChanges,
  isConversationEventChannelSwitch,
  isConversationEventDepartment,
  isConversationEventSummary,
  isConversationEventTag,
} from './EventsTypeGuards';
import { EventWrapper } from './EventWrapper';
import { NameAvatar } from './NameAvatar';
import { SummaryEvent } from './SummaryEvent';

// Passed in, not queried here: one row per event would mean one query observer per row.
type Props = { event: ConversationEvent; visitorName?: string };

const translationMapping = {
  resolve: 'conversation.resolve_event',
  reopen: 'conversation.reopen_event',
  trash: 'conversation.trash_event',
  spam: 'conversation.spam_event',
};

// The visitor moved the conversation when they tapped the link or wrote on the other channel.
const VISITOR_TRIGGERS = new Set(['user', 'message']);

const channelTypeKeys = {
  chat: 'conversation.channel_types.chat',
  voice: 'conversation.channel_types.voice',
  email: 'conversation.channel_types.email',
} as const;

const contextTagKeys = {
  added: 'conversation.context_tag_added',
  removed: 'conversation.context_tag_removed',
} as const;

export const Events = memo(({ event, visitorName }: Props) => {
  const { t } = useTranslation();
  const deskId = useDeskId();

  const { departments } = useDepartments(deskId);
  const { type, target, created, source_id } = event;
  const time = formatTimeOnly(created);

  // A move on the same integration only changes the channel type, e.g. a web-voice call to chat.
  const channelLabel = (target: ConversationEventChannelSwitch, side: 'prev' | 'next') => {
    const channel = side === 'prev' ? target.prev_channel : target.channel;
    const channelType = side === 'prev' ? target.prev_channel_type : target.channel_type;
    const name = t(getChannelKey(channel as IntegrationType));
    if (target.prev_integration_id !== target.integration_id || !channelType) {
      return name;
    }
    const typeKey = channelTypeKeys[channelType as keyof typeof channelTypeKeys];
    return `${name} · ${typeKey ? t(typeKey) : channelType}`;
  };

  const renderTags = (target: ConversationEventTag) => {
    let tagType: keyof typeof contextTagKeys | null = null;

    if (target?.added_tags.length) {
      tagType = 'added';
    } else if (target?.removed_tags.length) {
      tagType = 'removed';
    }
    const translationKey = tagType ? contextTagKeys[tagType] : null;
    const tagKey = tagType === 'added' ? 'added_tags' : 'removed_tags';

    return (
      <>
        <span className="text-sm leading-5 font-normal text-muted-foreground">
          {translationKey ? t(translationKey) : null}
        </span>
        <span className="text-sm leading-5 font-semibold text-muted-foreground">
          {target[tagKey][0]}
        </span>
      </>
    );
  };
  const name =
    (type === ConversationEvent.RESOLVE || type === ConversationEvent.REOPEN) && source_id == null
      ? visitorName
      : null;

  const renderEvent = () => {
    switch (type) {
      case ConversationEvent.UNASSIGN_BRAIN:
        return (
          <EventWrapper
            time={time}
            created={created}
            name={t('common.virtual_assistant')}
            userId="brain"
          >
            <span className="text-sm leading-5 font-normal text-muted-foreground">
              {t('conversation.left_conversation')}
            </span>
          </EventWrapper>
        );
      case ConversationEvent.ASSIGN_AGENT:
        if (isConversationEventAgent(target)) {
          if (target.prev_agent_id) {
            return (
              <EventWrapper time={time} created={created} userId={source_id}>
                <span className="text-sm leading-5 font-normal text-muted-foreground">
                  {t('conversation.assigned')}
                </span>
                <NameAvatar id={target.agent_id} />
              </EventWrapper>
            );
          }
          return (
            <EventWrapper time={time} created={created} userId={target.agent_id}>
              <span className="text-sm leading-5 font-normal text-muted-foreground">
                {t('conversation.joined_conversation')}
              </span>
            </EventWrapper>
          );
        }
        break;
      case ConversationEvent.UNASSIGN_AGENT:
        if (isConversationEventAgent(target)) {
          return (
            <EventWrapper time={time} created={created} userId={target.prev_agent_id}>
              <span className="text-sm leading-5 font-normal text-muted-foreground">
                {t('conversation.left_conversation')}
              </span>
            </EventWrapper>
          );
        }
        break;
      case ConversationEvent.ASSIGN_DEPARTMENT:
      case ConversationEvent.UNASSIGN_DEPARTMENT:
        if (isConversationEventDepartment(target)) {
          const departmentId =
            type === ConversationEvent.ASSIGN_DEPARTMENT
              ? target.department_id
              : target.prev_department_id;

          const translationKey =
            type === ConversationEvent.ASSIGN_DEPARTMENT
              ? 'conversation.assigned'
              : 'conversation.unassigned_event';

          return (
            <EventWrapper
              time={time}
              created={created}
              userId={source_id}
              name={t('common.system')}
            >
              <span className="text-sm leading-5 font-normal text-muted-foreground">
                {t(translationKey)}
              </span>
              <span className="text-sm leading-5 font-semibold text-muted-foreground">
                {departments?.find((d) => d.department_id === departmentId)?.name}
              </span>
              <span className="text-sm leading-5 font-normal text-muted-foreground">
                {t('conversation.department')}
              </span>
            </EventWrapper>
          );
        }
        break;
      case ConversationEvent.REOPEN:
      case ConversationEvent.TRASH:
      case ConversationEvent.SPAM:
      case ConversationEvent.RESOLVE:
        return (
          <EventWrapper time={time} created={created} userId={source_id} name={name}>
            <span className="text-sm leading-5 font-normal text-muted-foreground">
              {t(translationMapping[type])}
            </span>
          </EventWrapper>
        );

      case ConversationEvent.CONTEXT_USER:
        if (isConversationEventChanges(target)) {
          return (
            <EventWrapper time={time} created={created} userId={source_id}>
              <span className="text-sm leading-5 font-normal text-muted-foreground">
                {t('conversation.context_user', {
                  0: t(`common.${target.changes[0]}`).toLowerCase(),
                })}
              </span>
            </EventWrapper>
          );
        }
        break;
      case ConversationEvent.CONTEXT_TAG:
        if (isConversationEventTag(target)) {
          return (
            <EventWrapper time={time} created={created} userId={source_id}>
              {renderTags(target)}
            </EventWrapper>
          );
        }
        break;
      case ConversationEvent.CHANNEL_SWITCH:
        if (isConversationEventChannelSwitch(target)) {
          return (
            <EventWrapper
              time={time}
              created={created}
              userId={source_id}
              name={
                VISITOR_TRIGGERS.has(target.trigger)
                  ? (visitorName ?? t('common.visitor'))
                  : t('common.system')
              }
            >
              <span className="text-sm leading-5 font-normal text-muted-foreground">
                {t('conversation.channel_switch_event', {
                  0: channelLabel(target, 'prev'),
                  1: channelLabel(target, 'next'),
                })}
              </span>
            </EventWrapper>
          );
        }
        break;
      case ConversationEvent.SUMMARY:
        if (isConversationEventSummary(target)) {
          return <SummaryEvent summary={target.summary} userId={source_id} created={created} />;
        }
        break;
    }
  };

  return <>{renderEvent()}</>;
});

Events.displayName = 'Events';
